import React, { useCallback, useMemo, useState } from 'react';
import { useConnections } from '@integration-app/react';
import { ProviderSelect } from 'src/pages/Automations/components/ui/ProviderSelect';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { Agent, PostCallFinalizedSMS } from 'src/interfaces/agent.interface';
import { extractVariables } from 'src/utils/extractVariables';
import { POST_CALL_VARIABLES } from 'src/constants';
import { useIntegrationAppProviders } from 'src/hooks/useIntegrationAppProviders';

import { ConditionsList } from './conditions/ConditionsList';
import { UiCondition } from './conditions/types';
import {
  groupFilterToUiConditions,
  uiConditionsToGroupFilter,
} from './conditions/mappers';

type PostCallSmsSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};

const ensurePostCallSms = (
  a: Agent,
  providerId?: string,
): PostCallFinalizedSMS => ({
  provider_config: {
    destination_id:
      providerId ??
      (a.post_call_sms?.provider_config as { destination_id?: string })
        ?.destination_id ??
      '',
  },
  from_phone: a.post_call_sms?.from_phone ?? '',
  to_phone: a.post_call_sms?.to_phone ?? '',
  body: a.post_call_sms?.body ?? '',
});

export const PostCallSmsSection: React.FC<PostCallSmsSectionProps> = ({
  agent,
  setAgent,
  loading = false,
}) => {
  const { connections } = useConnections();
  const { smsProviders } = useIntegrationAppProviders();

  const [setupVisible, setSetupVisible] = useState<boolean>(
    !!agent.post_call_sms,
  );

  const providerId =
    ((agent.post_call_sms?.provider_config as { destination_id?: string })
      ?.destination_id ??
      '') ||
    '';

  const isDisabled = loading || (!agent.user_id && !!agent.id);

  const isProviderAuthed = useMemo(
    () =>
      !!connections.find(
        (c) => c.integration?.key === providerId && !c.disconnected,
      ),
    [connections, providerId],
  );

  const providersByValue = useMemo(
    () => Object.fromEntries(smsProviders.map((p) => [p.value, p.label])),
    [smsProviders],
  );

  const selectedProviderOption = useMemo(
    () => ({
      value: providerId,
      label: providersByValue[providerId] ?? 'Select a provider',
    }),
    [providerId, providersByValue],
  );

  const updatePostCallSms = useCallback(
    (patch: Partial<PostCallFinalizedSMS>) =>
      setAgent((prev) => {
        const base = ensurePostCallSms(
          prev,
          patch.provider_config?.destination_id,
        );
        return {
          ...prev,
          post_call_sms: {
            ...base,
            ...patch,
            provider_config: {
              ...base.provider_config,
              ...patch.provider_config,
            },
          },
        };
      }),
    [setAgent],
  );

  const handleProviderChange = useCallback(
    (providerOption: { value: string; label?: string }) => {
      updatePostCallSms({
        provider_config: { destination_id: providerOption.value },
      });
      if (!setupVisible) setSetupVisible(true);
    },
    [updatePostCallSms, setupVisible],
  );

  const handleRemoveSetup = useCallback(() => {
    setSetupVisible(false);
    setAgent((prev) => ({ ...prev, post_call_sms: null }));
  }, [setAgent]);

  // Conditions UI state (owned locally after initial mount)
  const [smsConditions, setSmsConditions] = useState<UiCondition[]>(() =>
    groupFilterToUiConditions(agent.post_call_sms?.conditions),
  );

  const handleConditionsChange = useCallback(
    (next: UiCondition[]) => {
      setSmsConditions(next);
      const filter = uiConditionsToGroupFilter(next);
      updatePostCallSms({ conditions: filter });
    },
    [updatePostCallSms],
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between items-start gap-4">
        <h4>Send SMS</h4>
        {setupVisible && (
          <Button
            variant="outlined"
            color="error"
            data-testid="button-dont-send-sms"
            onClick={handleRemoveSetup}
            className="whitespace-nowrap"
            size="small"
          >
            {"Don't Send an SMS"}
          </Button>
        )}
      </div>

      {!setupVisible ? (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-8 text-center space-y-4">
          <Button
            variant="outlined"
            color="primary"
            data-testid="button-configure-sms"
            onClick={() => {
              // Initialize post_call_sms immediately so save validation runs
              updatePostCallSms({});
              setSetupVisible(true);
            }}
            className="whitespace-nowrap"
            size="small"
          >
            Configure SMS
          </Button>
        </div>
      ) : (
        <>
          <ProviderSelect
            providers={smsProviders}
            selectedProvider={selectedProviderOption}
            onChange={handleProviderChange}
            isAuthenticated={isProviderAuthed}
            label="SMS Provider"
            htmlFor="sms-provider"
            className="w-full bg-neutral-0"
            compact
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="From Number"
              type="text"
              id="smsFromPhone"
              placeholder="(e.g., +15551234567)"
              value={agent.post_call_sms?.from_phone || ''}
              onChange={(e) =>
                updatePostCallSms({ from_phone: e.target.value })
              }
              disabled={isDisabled}
            />
            <Input
              label="To Number"
              type="text"
              id="smsToPhone"
              placeholder="(e.g., +15551234567)"
              value={agent.post_call_sms?.to_phone || ''}
              onChange={(e) => updatePostCallSms({ to_phone: e.target.value })}
              disabled={isDisabled}
            />
          </div>
          <div className="pb-6">
            <Label>SMS Message</Label>
            <PromptTextArea
              value={agent.post_call_sms?.body || ''}
              onChange={(e) => updatePostCallSms({ body: e.target.value })}
              variables={Array.from(
                new Set([
                  ...POST_CALL_VARIABLES,
                  ...extractVariables(agent.post_call_sms?.body || ''),
                ]),
              )}
              placeholder={
                'Thank you for calling {first_name}! We will get back to you soon. If you have any questions reply to this message.'
              }
              rows={6}
              loading={loading}
            />
          </div>
          <div className="mt-2">
            <ConditionsList
              conditions={smsConditions}
              onChange={handleConditionsChange}
              disabled={isDisabled}
              labels={{
                withConditionsDescription:
                  'All conditions must be satisfied for an SMS to be sent',
                noConditionsDescription:
                  'Add conditions to control when an SMS is sent',
                noConditionsStateText:
                  'No conditions set. SMS messages will be sent after every call.',
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
